/**
 * CloudEmergencyTable — real-time emergency alerts table for Cloud 5.0 platform.
 * Source: activeCloudAlarms from NotificationContext.
 *
 * Columns: Event Start, Duration, Category/Type, Device/Serial, Description,
 *          Area, Resident, Accepted By/Time, Device Cleared, Status
 *
 * Schema uses snake_case fields (event_id, event_datetime_utc, event_category, etc.)
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow } from '../../../../libs.jsx';
import AlertStatusBadge from './AlertStatusBadge.jsx';
import AlertTypeBadge from './AlertTypeBadge.jsx';

/** Hook that ticks every second to force duration re-renders */
function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const CloudEmergencyTable = ({ data, sortColumn, sortOrder, onSort }) => {
    useTick(1000); // Re-render every second for live duration updates

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    const formatUtcToLocal = (utcStr) => {
        if (!utcStr) return '\u2014';
        try {
            const d = new Date(utcStr.endsWith('Z') ? utcStr : utcStr + 'Z');
            return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
        } catch { return utcStr; }
    };

    const getDeviceLink = (row) => {
        const mfg = parseInt(row.manufacture_id, 10);
        if (mfg === 1) return `/profiles/inovonics/${row.device_id}`;
        if (mfg === 4) return `/profiles/palcare/${row.device_id}`;
        return null;
    };

    const getBorderColor = (category) => {
        const cat = (category || '').toLowerCase();
        if (cat === 'emergency') return '#e63757';
        if (cat === 'maintenance') return '#ffc107';
        return '#0081C7';
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('event_datetime_utc')}>Event Start{sortIcon('event_datetime_utc')}</th>
                        <th className="dp-th">Duration</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('event_category')}>Category / Type{sortIcon('event_category')}</th>
                        <th className="dp-th">Device / Serial</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('device_description')}>Description{sortIcon('device_description')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('area_name')}>Area{sortIcon('area_name')}</th>
                        <th className="dp-th">Resident</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('accepted_display')}>Accepted By / Time{sortIcon('accepted_display')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('ack_datetime_utc')}>Device Cleared{sortIcon('ack_datetime_utc')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('status')}>Status{sortIcon('status')}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={10}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">{'\u2713'}</div>
                                    <div className="na-empty__text">No active emergency alerts</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row) => {
                        const deviceLink = getDeviceLink(row);
                        return (
                            <tr key={row.event_id} className="dp-tr na-row-border" style={{ borderLeftColor: getBorderColor(row.event_category) }}>
                                <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcToLocal(row.event_datetime_utc)}</td>
                                <td className="dp-td"><span className="na-duration">{durationToNow(row.event_datetime_utc)}</span></td>
                                <td className="dp-td">
                                    <AlertTypeBadge alertType={row.event_category} alertSubType={row.event_type} />
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {deviceLink ? (
                                            <Link to={deviceLink} className="ar-link">{row.device_name || '\u2014'}</Link>
                                        ) : (<span>{row.device_name || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.manufacture_uuid || ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td">{row.device_description || '\u2014'}</td>
                                <td className="dp-td">
                                    {row.area_id ? (
                                        <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{row.area_name || '\u2014'}</Link>
                                    ) : (row.area_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    {row.resident_id ? (
                                        <Link to={`/profiles/resident/${row.resident_id}`} className="ar-link">{row.resident_name || '\u2014'}</Link>
                                    ) : (row.resident_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {row.accepted_user_id ? (
                                            <Link to={`/profiles/user/${row.accepted_user_id}`} className="ar-link">{row.accepted_display || '\u2014'}</Link>
                                        ) : (<span>{row.accepted_display || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.accepted_datetime_utc ? formatUtcToLocal(row.accepted_datetime_utc) : ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcToLocal(row.ack_datetime_utc)}</td>
                                <td className="dp-td"><AlertStatusBadge status={row.status} /></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default CloudEmergencyTable;
